import logo from '@/assets/images/logo_light_sm.png';
import Button from '@/components/General/Button';
import { CodeHighlight } from '@mantine/code-highlight';
import { Box, Center, Container, Group, Image, Paper, Stack, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconRefresh } from '@tabler/icons-react';
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Capture errors with Posthog regardless of cookie consent
    // Error tracking is enabled by default in main.jsx
    if (window.posthog) {
      window.posthog.captureException(error, {
        componentStack: errorInfo?.componentStack,
        stack: error?.stack,
      });
    }
  }

  resetError = () => {
    this.setState({ error: null, errorInfo: null });
    // You might also want to reload or navigate somewhere safe
  };

  render() {
    const { error, errorInfo } = this.state;

    if (error) {
      return (
        <Center h="100vh" bg="gray.9">
          <Container
            w={import.meta.env.VITE_APP_ENV === 'development' ? 720 : 480}
            px="md"
            py="xl"
            style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
          >
            <Box mb="xl" style={{ display: 'flex', justifyContent: 'center' }}>
              <Image src={logo} alt={import.meta.env.VITE_APP_NAME} w={160} />
            </Box>

            <Paper withBorder shadow="xl" radius="md" p="xl">
              <Stack align="center" spacing="lg">
                <Title order={1} c="gray.9">
                  Something went wrong
                </Title>
                <Text c="gray.9" ta="center">
                  An unexpected error occurred. You can try going back to the dashboard.
                </Text>
                <ErrorBoundaryActions />

                {import.meta.env.VITE_APP_ENV === 'development' && (
                  <CodeHighlight
                    withCopyButton
                    withExpandButton
                    defaultExpanded={false}
                    expandCodeLabel="Show Full Stack Trace"
                    collapseCodeLabel="Show Less"
                    maxCollapsedHeight={300}
                    code={`${error?.toString()}\n${errorInfo?.componentStack || ''}`}
                    language="tsx"
                    mah={500}
                    w={'100%'}
                    bdrs={'sm'}
                    bd={'1px solid var(--mantine-color-gray-2)'}
                    mt="md"
                    style={{
                      boxShadow: 'var(--mantine-shadow-md)',
                    }}
                  />
                )}
              </Stack>
            </Paper>

            <Box mt="xl" component="footer" style={{ textAlign: 'center', color: '#999', fontSize: 14 }}>
              &copy; {new Date().getFullYear()} LoveInvoice
            </Box>
          </Container>
        </Center>
      );
    }

    return this.props.children;
  }
}

function ErrorBoundaryActions() {
  return (
    <Group gap="sm">
      <Button
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => {
          if (window.history.length > 1) {
            window.history.back();
          } else {
            window.location.href = '/';
          }
        }}
      >
        Back to Dashboard
      </Button>
      <Button
        variant="outline"
        leftSection={<IconRefresh size={16} />}
        onClick={() => {
          window.location.reload();
        }}
      >
        Reload Page
      </Button>
    </Group>
  );
}

export default ErrorBoundary;
